import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CITY_LANDMARK_LOCATIONS } from '@/lib/defaultLocations';

export interface TourCity {
  city_name: string;
  region: string;
  country: string;
  latitude: number;
  longitude: number;
  tour_count: number;
  display_name: string;
}


interface TourCitySelectorProps {
  onCitySelect: (city: TourCity) => void;
  selectedCity?: TourCity | null;
  variant?: 'minibid';
}

export const TourCitySelector: React.FC<TourCitySelectorProps> = ({
  onCitySelect,
  selectedCity,
  variant,
}) => {
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState<TourCity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');

  const normalizeLocationText = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  // Build city catalog using the same sources as navbar city selector:
  // predefined city landmarks + store-backed city locations.
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const baseCities: TourCity[] = CITY_LANDMARK_LOCATIONS.map((location) => ({
          city_name: location.city,
          region: location.country,
          country: location.country,
          latitude: location.latitude,
          longitude: location.longitude,
          tour_count: 0,
          display_name: location.displayName,
        }));

        const dedupedByKey = new Map<string, TourCity>();
        for (const city of baseCities) {
          const key = `${normalizeLocationText(city.city_name)}|${normalizeLocationText(city.country)}`;
          dedupedByKey.set(key, city);
        }

        const { data: stores, error } = await supabase
          .from('stores')
          .select('location_display_name, latitude, longitude')
          .not('location_display_name', 'is', null)
          .limit(500);

        if (error) {
          console.warn('[TourCitySelector] Could not load store-backed cities:', error);
        } else {
          for (const row of stores || []) {
            const displayName = (row.location_display_name || '').trim();
            const cityName = displayName.split(',')[0]?.trim();
            if (!cityName) continue;

            const parts = displayName
              .split(',')
              .map((part) => part.trim())
              .filter(Boolean);

            const country = parts.length > 1 ? parts[parts.length - 1] : 'Otros';
            const latitude = Number(row.latitude);
            const longitude = Number(row.longitude);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;

            const key = `${normalizeLocationText(cityName)}|${normalizeLocationText(country)}`;
            if (dedupedByKey.has(key)) continue;

            dedupedByKey.set(key, {
              city_name: cityName,
              region: country,
              country,
              latitude,
              longitude,
              tour_count: 0,
              display_name: displayName,
            });
          }
        }

        setCities(Array.from(dedupedByKey.values()));
      } catch (err) {
        console.error('❌ [TourCitySelector] Exception:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  // Filter cities based on search
  const filteredCities = cities.filter(city =>
    city.display_name.toLowerCase().includes(searchValue.toLowerCase()) ||
    city.city_name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSelect = (city: TourCity) => {
    console.log('🎯 [TourCitySelector] City selected:', {
      city: city.city_name,
      tours: city.tour_count,
      coords: { lat: city.latitude, lng: city.longitude },
    });
    onCitySelect(city);
    setOpen(false);
  };

  return (
    <div className="space-y-3">
      {/* Label eliminado para evitar duplicado, ahora lo maneja ActivitiesSidebar */}
      
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={variant === 'minibid' ? 'ghost' : 'outline'}
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between h-12 px-3 transition-colors',
              variant === 'minibid'
                ? 'rounded-2xl border border-primary/20 bg-primary/5 text-primary font-semibold shadow-sm hover:bg-primary/20 hover:text-primary'
                : 'rounded-xl border-border/70 bg-muted/30 hover:bg-muted/50'
            )}
            disabled={loading}
          >
            {loading ? (
              <span className="flex items-center gap-2 text-muted-foreground">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Cargando ciudades...
              </span>
            ) : selectedCity ? (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary" />
                {selectedCity.city_name}
              </span>
            ) : (
              <span className="text-muted-foreground">Selecciona una ciudad...</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          align="start"
          className={cn(
            'z-[10010] w-[var(--radix-popover-trigger-width)] p-0',
            variant === 'minibid'
              ? 'rounded-2xl border-primary/20 shadow-lg bg-background'
              : 'rounded-xl border-border/70'
          )}
        >
          <Command>
            <CommandInput
              placeholder="Buscar ciudad en Minibid..."
              value={searchValue}
              onValueChange={setSearchValue}
              className="border-none"
            />
            <CommandEmpty className="text-center py-6 text-muted-foreground">
              No encontramos ciudades con ese nombre.
            </CommandEmpty>
            <CommandList>
              <CommandGroup>
                {filteredCities.map((city) => (
                  <CommandItem
                    key={`${city.city_name}-${city.latitude}`}
                    value={city.display_name}
                    onSelect={() => handleSelect(city)}
                    className={cn(
                      'cursor-pointer rounded-md mx-1 my-0.5 transition-colors',
                      variant === 'minibid'
                        ? "data-[selected='true']:bg-primary/15 data-[selected='true']:text-primary hover:bg-primary/10 hover:text-primary"
                        : "data-[selected='true']:bg-muted data-[selected='true']:text-foreground hover:bg-muted"
                    )}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedCity?.city_name === city.city_name
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{city.city_name}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>


    </div>
  );
};

export default TourCitySelector;
